import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import axios from "../../config/axios";

const useStyles = makeStyles({
  metricCard: {
    padding: "16px 20px",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  metricHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 6,
  },
  metricIconBox: {
    width: 42,
    height: 42,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flexShrink: 0,
  },
  metricLabel: {
    fontSize: 13,
    color: "#888",
    margin: 0,
  },
  metricValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "#333",
    margin: 0,
    lineHeight: 1.1,
  },
  metricSmall: {
    fontSize: 14,
    fontWeight: 400,
    color: "#aaa",
  },
  metricFooter: {
    fontSize: 12,
    color: "#999",
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 6,
  },
  sectionCard: {
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
    overflow: "hidden",
    marginBottom: 0,
  },
  sectionHeader: {
    padding: "14px 20px",
    color: "#fff",
    "& h4": {
      margin: 0,
      fontWeight: 600,
      fontSize: 15,
    },
    "& p": {
      margin: 0,
      fontSize: 12,
      opacity: 0.8,
    },
  },
  sectionBody: {
    padding: "16px 20px",
  },
});

const statusColor = {
  processing: { bg: "#fff3e0", color: "#e65100", label: "Pendente" },
  confirmed: { bg: "#e3f2fd", color: "#1565c0", label: "Confirmado" },
  completed: { bg: "#e8f5e9", color: "#2e7d32", label: "Concluído" },
  cancelled: { bg: "#fce4ec", color: "#c62828", label: "Cancelado" },
};

function StatusChip({ status }) {
  const s = statusColor[status] || { bg: "#f5f5f5", color: "#333", label: status };
  return (
    <Chip
      label={s.label}
      size="small"
      style={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, borderRadius: 4 }}
    />
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("pt-PT");
}

export default function Dashboard() {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subsTab, setSubsTab] = useState(0);

  useEffect(() => {
    axios.get("/admin/dashboard")
      .then(res => setData(res.data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
        <CircularProgress />
      </div>
    );
  }

  if (!data) {
    return <div style={{ padding: 24, color: "#c62828" }}>Erro ao carregar métricas.</div>;
  }

  const subsData = [data.subs_ending_30, data.subs_ending_60, data.subs_ending_90];
  const subsLabels = [
    `30 dias (${data.subs_ending_30.length})`,
    `60 dias (${data.subs_ending_60.length})`,
    `90 dias (${data.subs_ending_90.length})`,
  ];
  const totalSubsEnding = data.subs_ending_30.length + data.subs_ending_60.length + data.subs_ending_90.length;

  const metrics = [
    {
      icon: "euro",
      iconBg: "#4caf50",
      label: "Faturação recorrente mensal",
      value: `${data.mrr.toFixed(2)}€`,
      small: "/mês",
      footer: `${data.active_clients} ${data.active_clients === 1 ? "cliente" : "clientes"} ativos`,
      footerIcon: "people",
    },
    {
      icon: "straighten",
      iconBg: "#2196f3",
      label: "m² em armazém",
      value: `${data.total_m2.toFixed(1)}`,
      small: "m²",
      footer: "Ocupação total atual",
      footerIcon: "store",
    },
    {
      icon: "update",
      iconBg: "#ff9800",
      label: "Subscrições a terminar",
      value: `${totalSubsEnding}`,
      small: "",
      footer: "Nos próximos 90 dias",
      footerIcon: "today",
    },
    {
      icon: "pending_actions",
      iconBg: "#f44336",
      label: "Ações pendentes",
      value: `${data.pending_pickups.length + data.scheduled_returns.length}`,
      small: "",
      footer: `${data.pending_pickups.length} recolhas · ${data.scheduled_returns.length} devoluções`,
      footerIcon: "local_shipping",
    },
  ];

  return (
    <div style={{ padding: "0 4px" }}>
      {/* ── Metric cards ── */}
      <GridContainer>
        {metrics.map((m, i) => (
          <GridItem xs={12} sm={6} md={3} key={i}>
            <Paper className={classes.metricCard} style={{ marginBottom: 24 }}>
              <div className={classes.metricHeader}>
                <div className={classes.metricIconBox} style={{ backgroundColor: m.iconBg }}>
                  <Icon style={{ fontSize: 22 }}>{m.icon}</Icon>
                </div>
                <p className={classes.metricLabel}>{m.label}</p>
              </div>
              <p className={classes.metricValue}>
                {m.value} <span className={classes.metricSmall}>{m.small}</span>
              </p>
              <div className={classes.metricFooter}>
                <Icon style={{ fontSize: 14 }}>{m.footerIcon}</Icon>
                {m.footer}
              </div>
            </Paper>
          </GridItem>
        ))}
      </GridContainer>

      {/* ── Subscriptions ending ── */}
      <GridContainer>
        <GridItem xs={12}>
          <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
            <div className={classes.sectionHeader} style={{ backgroundColor: "#ff9800" }}>
              <h4>Subscrições a terminar</h4>
              <p>Clientes cujo período de subscrição termina em breve</p>
            </div>
            <div className={classes.sectionBody}>
              <Tabs
                value={subsTab}
                onChange={(_, v) => setSubsTab(v)}
                indicatorColor="primary"
                textColor="primary"
              >
                {subsLabels.map((label, i) => <Tab key={i} label={label} />)}
              </Tabs>
              {subsData[subsTab].length === 0 ? (
                <p style={{ color: "#666", padding: "16px 0", margin: 0 }}>Nenhuma subscrição a terminar neste período.</p>
              ) : (
                <TableContainer style={{ marginTop: 16 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Cliente</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell align="center"><strong>m²</strong></TableCell>
                        <TableCell align="center"><strong>Valor/mês</strong></TableCell>
                        <TableCell align="center"><strong>Termina em</strong></TableCell>
                        <TableCell align="center"><strong>Frequência</strong></TableCell>
                        <TableCell align="center"><strong>Pedido</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subsData[subsTab].map((s, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{s.user_name}</TableCell>
                          <TableCell style={{ fontSize: 12, color: "#666" }}>{s.user_email}</TableCell>
                          <TableCell align="center">{s.m2 || "—"}</TableCell>
                          <TableCell align="center" style={{ fontWeight: 600 }}>
                            {s.monthly_amount ? `${s.monthly_amount.toFixed(2)}€` : "—"}
                          </TableCell>
                          <TableCell align="center" style={{ color: "#e65100", fontWeight: 600 }}>
                            {formatDate(s.end_date)}
                          </TableCell>
                          <TableCell align="center" style={{ fontSize: 12 }}>{s.payment_frequency || "—"}</TableCell>
                          <TableCell align="center">
                            {s.order_id ? (
                              <Link to={`/admin/orders/${s.order_id}`} style={{ color: "#1565c0" }}>
                                #{s.order_id}
                              </Link>
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </Paper>
        </GridItem>
      </GridContainer>

      {/* ── Pending pickups + Scheduled returns ── */}
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
            <div className={classes.sectionHeader} style={{ backgroundColor: "#f44336" }}>
              <h4>Recolhas pendentes ({data.pending_pickups.length})</h4>
              <p>Pedidos aguardam confirmação ou recolha</p>
            </div>
            <div className={classes.sectionBody}>
              {data.pending_pickups.length === 0 ? (
                <p style={{ color: "#666", margin: 0 }}>Sem recolhas pendentes.</p>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Cliente</strong></TableCell>
                        <TableCell align="center"><strong>Estado</strong></TableCell>
                        <TableCell align="center"><strong>Data recolha</strong></TableCell>
                        <TableCell align="center"><strong>Pedido</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.pending_pickups.map((p, i) => (
                        <TableRow key={i} hover>
                          <TableCell>
                            <div style={{ fontWeight: 600 }}>{p.user_name}</div>
                            <div style={{ fontSize: 11, color: "#888" }}>{p.user_email}</div>
                          </TableCell>
                          <TableCell align="center"><StatusChip status={p.status} /></TableCell>
                          <TableCell align="center" style={{ fontSize: 13 }}>
                            {p.pickup_date ? formatDate(p.pickup_date.split("::")[0]) : "—"}
                          </TableCell>
                          <TableCell align="center">
                            <Link to={`/admin/orders/${p.order_id}`} style={{ color: "#1565c0" }}>
                              #{p.order_id}
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </Paper>
        </GridItem>

        <GridItem xs={12} sm={12} md={6}>
          <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
            <div className={classes.sectionHeader} style={{ backgroundColor: "#2196f3" }}>
              <h4>Devoluções agendadas ({data.scheduled_returns.length})</h4>
              <p>Pedidos de devolução por processar</p>
            </div>
            <div className={classes.sectionBody}>
              {data.scheduled_returns.length === 0 ? (
                <p style={{ color: "#666", margin: 0 }}>Sem devoluções agendadas.</p>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Cliente</strong></TableCell>
                        <TableCell align="center"><strong>Estado</strong></TableCell>
                        <TableCell align="center"><strong>Data devolução</strong></TableCell>
                        <TableCell align="center"><strong>Tipo</strong></TableCell>
                        <TableCell align="center"><strong>Pedido</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.scheduled_returns.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell>
                            <div style={{ fontWeight: 600 }}>{r.user_name}</div>
                            <div style={{ fontSize: 11, color: "#888" }}>{r.user_email}</div>
                          </TableCell>
                          <TableCell align="center"><StatusChip status={r.status} /></TableCell>
                          <TableCell align="center" style={{ fontSize: 13 }}>
                            {r.return_date ? formatDate(r.return_date.split("::")[0]) : "—"}
                          </TableCell>
                          <TableCell align="center" style={{ fontSize: 12 }}>
                            {r.partial ? "Parcial" : "Total"}
                          </TableCell>
                          <TableCell align="center">
                            <Link to={`/admin/return-orders/${r.return_id}`} style={{ color: "#1565c0" }}>
                              #{r.return_id}
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </Paper>
        </GridItem>
      </GridContainer>
    </div>
  );
}
